import PropTypes from 'prop-types';

import {Badge, Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import Swatch from '@salesforce/retail-react-app/app/components/swatch-group/swatch';

/**
 * Inline Swatch Card Renderer
 *
 * Renders variation attributes as horizontal inline cards.
 * Each card contains: thumbnail (top), name (center), and optional "Popular" badge.
 */
export function CardAttributeValue({
  attribute,
  attributeValue,
  index,
}) {
  // const {valuesWithPrices = []} = attribute;
  const {selectedValue} = attribute;
  const {href, image, name, orderable, value, badges} = attributeValue;
  // const styles = useMultiStyleConfig('InlineSwatchCard', {});

  const hasSelection = Boolean(selectedValue?.value);
  const isSelected = selectedValue?.value === value;
  const isFirst = index === 0;

  // To mimic the behavior of a native radio input, only
  // one swatch should receive tab focus; the rest can be
  // selected using arrow keys when the swatch group has
  // focus. The focused element is the selected option or
  // the first in the group, if no option is selected.
  // This is a slight difference, for simplicity, from the
  // native element, where the first element is focused on
  // `Tab` and the _last_ element is focused on `Shift+Tab`
  const isFocusable = isSelected || (!hasSelection && isFirst);

  // Only show badge if this swatch is selected AND isPopular flag is true
  // Ensure isPopular is boolean true (not truthy)
  const showPopularBadge = isSelected && badges?.popular === true;

  const styles = useMultiStyleConfig('SwatchGroup', {
    variant: 'card',
    disabled: !orderable,
    selected: isSelected,
  });

  return (
    <Swatch
      key={value}
      disabled={!orderable}
      href={href}
      isFocusable={isFocusable}
      name={name}
      selected={isSelected}
      value={value}
      variant="card"
    >
      {showPopularBadge && (
        <Badge {...styles.popularBadge}>
          Popular
        </Badge>
      )}

      <Box {...styles.thumbnail}>
        {image ? (
          <Box
            {...styles.thumbnailImage}
            backgroundColor={name.toLowerCase()}
            backgroundImage={`url(${image.disBaseLink || image.link})`}
          />
        ) : (
          <Box {...styles.thumbnailFallback}>
            <Text {...styles.thumbnailFallbackText}>{name}</Text>
          </Box>
        )}
      </Box>

      <Text {...styles.label}>{name}</Text>
    </Swatch>
  );
}

CardAttributeValue.displayName = 'CardAttributeValue';

CardAttributeValue.propTypes = {
  attributeValue: PropTypes.object.isRequired,
  attribute: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};
